/**
 * Bollinger Band variants for the Charts "BB" overlay.
 *
 *   traditional          — SMA(displayClose, p) ± mult·σ(displayClose)   [classic; unchanged]
 *   liner                — EMA(CLH close, p)    ± mult·σ(CLH close)
 *   customLiner          — mid = EMA(CLH close, p);
 *                          upper = EMA(bodyTop, p)    + mult·σ(bodyTop),
 *                          lower = EMA(bodyBottom, p) − mult·σ(bodyBottom)   [asymmetric]
 *   customLinerWeighted  — as customLiner but VWMA + volume-weighted σ
 *                          (falls back to EMA when the symbol carries no volume).
 *
 * CLH = the Custom-LinReg-HA transform (same one the chart's CLH candle type and
 * the RockBand/Bhairav engines use), computed from the RAW price series so the
 * bands are independent of the user's selected DISPLAY candle type. bodyTop =
 * max(open, close), bodyBottom = min(open, close) of the transformed candle.
 *
 * All arrays are returned aligned to `displayCandles` indexes; warm-up bars (and
 * any bar without a full window) are `null`.
 */
import type { CandleRow } from '../components/charts/TradingChart';
import { transformCandles } from './candleTransforms';
import { emaSeries, vwmaSeries } from './jhm';

export type BollingerVariant = 'traditional' | 'liner' | 'customLiner' | 'customLinerWeighted';

export interface BollingerCfg {
  variant: BollingerVariant;
  period: number;
  stdMult: number;
}

export interface BollingerBands {
  upper: (number | null)[];
  middle: (number | null)[];
  lower: (number | null)[];
}

/** Simple moving average (NaN for warm-up). */
function smaArr(v: number[], p: number): number[] {
  const out = new Array<number>(v.length).fill(NaN);
  let sum = 0;
  for (let i = 0; i < v.length; i++) {
    sum += v[i];
    if (i >= p) sum -= v[i - p];
    if (i >= p - 1) out[i] = sum / p;
  }
  return out;
}

/** Population standard deviation of `x` around `basis[i]` over the trailing
 *  p-window; volume-weighted when `weights` is supplied. */
function rollStd(x: number[], basis: number[], p: number, weights?: number[]): number[] {
  const out = new Array<number>(x.length).fill(NaN);
  for (let i = p - 1; i < x.length; i++) {
    const b = basis[i];
    if (!Number.isFinite(b)) continue;
    let num = 0, den = 0, ok = true;
    for (let j = i - p + 1; j <= i; j++) {
      const xv = x[j];
      if (!Number.isFinite(xv)) { ok = false; break; }
      const w = weights ? (weights[j] || 0) : 1;
      num += w * (xv - b) ** 2;
      den += w;
    }
    if (ok && den > 0) out[i] = Math.sqrt(num / den);
  }
  return out;
}

const finalize = (v: number[]): (number | null)[] => v.map(x => (Number.isFinite(x) ? x : null));
const band = (basis: number[], sd: number[], m: number, sign: 1 | -1): (number | null)[] =>
  finalize(basis.map((b, i) => b + sign * m * sd[i]));

/**
 * @param displayCandles the on-screen (display-transformed) candles — Traditional uses their close.
 * @param rawCandles     the raw (untransformed) candles — the Liner variants transform these to CLH.
 *                       Falls back to displayCandles when raw is unavailable / mismatched.
 */
export function computeBollingerBands(
  displayCandles: CandleRow[],
  rawCandles: CandleRow[] | undefined,
  cfg: BollingerCfg,
): BollingerBands {
  const n = displayCandles.length;
  const empty: BollingerBands = { upper: new Array(n).fill(null), middle: new Array(n).fill(null), lower: new Array(n).fill(null) };
  if (n < 2) return empty;
  const p = Math.max(2, Math.floor(cfg.period) || 20);
  const m = Number.isFinite(cfg.stdMult) ? cfg.stdMult : 2;

  if (cfg.variant === 'traditional') {
    const closes = displayCandles.map(c => Number(c.close));
    const basis = smaArr(closes, p);
    const sd = rollStd(closes, basis, p);
    return { upper: band(basis, sd, m, 1), middle: finalize(basis), lower: band(basis, sd, m, -1) };
  }

  // ── Liner variants — CLH transform of the RAW price series ──
  const src = (rawCandles && rawCandles.length === n) ? rawCandles : displayCandles;
  const clh = transformCandles(src as any, 'CLH') as unknown as CandleRow[];
  const clhClose = clh.map(c => Number(c.close));
  const vols = src.map(c => Number((c as any).volume) || 0);

  if (cfg.variant === 'liner') {
    const basis = emaSeries(clhClose, p);
    const sd = rollStd(clhClose, basis, p);
    return { upper: band(basis, sd, m, 1), middle: finalize(basis), lower: band(basis, sd, m, -1) };
  }

  // customLiner / customLinerWeighted — per-side EMA/VWMA of the candle body extremes.
  const bodyTop = clh.map(c => Math.max(Number(c.open), Number(c.close)));
  const bodyBottom = clh.map(c => Math.min(Number(c.open), Number(c.close)));
  const wantWeighted = cfg.variant === 'customLinerWeighted';
  const hasVol = wantWeighted && vols.some(v => v > 0);
  const avg = (v: number[]) => (hasVol ? vwmaSeries(v, vols, p) : emaSeries(v, p));
  const w = hasVol ? vols : undefined;

  const midBasis = avg(clhClose);
  const upBasis = avg(bodyTop);
  const loBasis = avg(bodyBottom);
  const sdUp = rollStd(bodyTop, upBasis, p, w);
  const sdLo = rollStd(bodyBottom, loBasis, p, w);
  return { upper: band(upBasis, sdUp, m, 1), middle: finalize(midBasis), lower: band(loBasis, sdLo, m, -1) };
}
